// src/pages/Reports/AgingReport.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '24px',
        maxWidth: '1200px',
        margin: '0 auto'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    title: {
        fontSize: '28px',
        fontWeight: '800',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '20px',
        marginBottom: '32px'
    },
    card: {
        padding: '24px',
        borderRadius: '16px',
        background: 'white',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e2e8f0'
    },
    cardLabel: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: '8px'
    },
    cardValue: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#0f172a'
    },
    tableContainer: {
        background: 'white',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '14px'
    },
    th: {
        background: '#f8fafc',
        padding: '16px',
        textAlign: 'left',
        fontWeight: '600',
        color: '#475569',
        borderBottom: '1px solid #e2e8f0'
    },
    td: {
        padding: '16px',
        borderBottom: '1px solid #f1f5f9',
        color: '#1e293b'
    },
    badge: {
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '600'
    },
    redBadge: { background: '#fef2f2', color: '#dc2626' },
    orangeBadge: { background: '#fff7ed', color: '#ea580c' },
    blueBadge: { background: '#eff6ff', color: '#2563eb' },
    greenBadge: { background: '#f0fdf4', color: '#06C167' }
};

function AgingReport() {
    const { userData } = useAuth();
    const [customers, setCustomers] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userData?.companyId) return;
        const companyId = userData.companyId;

        const unsubCustomers = onSnapshot(collection(db, 'companies', companyId, 'customers'), snap => {
            setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        const unsubInvoices = onSnapshot(query(collection(db, 'companies', companyId, 'invoices'), orderBy('invoiceDate', 'desc')), snap => {
            setInvoices(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        setLoading(false);
        return () => { unsubCustomers(); unsubInvoices(); };
    }, [userData]);

    const agingData = useMemo(() => {
        const today = new Date();
        const result = customers.map(customer => {
            const balance = customer.balance || 0;
            // Negative balance means customer owes us money in this system (usually)
            // Let's assume balance < 0 is debt.
            const debt = balance < 0 ? Math.abs(balance) : 0;

            const buckets = {
                current: 0, // 0-30
                overdue30: 0, // 31-60
                overdue60: 0, // 61-90
                overdue90: 0  // 90+
            };

            if (debt > 0) {
                // Find all invoices for this customer
                const customerInvoices = invoices.filter(inv => inv.customerId === customer.id);
                let remainingDebt = debt;

                // Sort invoices by date descending (newest first)
                // We assume the debt is composed of the most recent invoices (simplified FIFO)
                customerInvoices.forEach(inv => {
                    if (remainingDebt <= 0) return;

                    const invDate = inv.invoiceDate?.seconds ? new Date(inv.invoiceDate.seconds * 1000) : new Date();
                    const diffDays = Math.floor((today - invDate) / (1000 * 60 * 60 * 24));
                    
                    const amount = Math.min(inv.total, remainingDebt);
                    
                    if (diffDays <= 30) buckets.current += amount;
                    else if (diffDays <= 60) buckets.overdue30 += amount;
                    else if (diffDays <= 90) buckets.overdue60 += amount;
                    else buckets.overdue90 += amount;

                    remainingDebt -= amount;
                });

                // If there's still debt after checking all invoices, put it in the oldest bucket
                if (remainingDebt > 0) {
                    buckets.overdue90 += remainingDebt;
                }
            }

            return {
                ...customer,
                debt,
                buckets
            };
        }).filter(c => c.debt > 0).sort((a,b) => b.debt - a.debt);

        const totals = result.reduce((acc, c) => ({
            current: acc.current + c.buckets.current,
            overdue30: acc.overdue30 + c.buckets.overdue30,
            overdue60: acc.overdue60 + c.buckets.overdue60,
            overdue90: acc.overdue90 + c.buckets.overdue90,
            debt: acc.debt + c.debt
        }), { current: 0, overdue30: 0, overdue60: 0, overdue90: 0, debt: 0 });

        return { list: result, totals };
    }, [customers, invoices]);

    if (loading) return <div style={styles.container}>Yükleniyor...</div>;

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>Borç Yaşlandırma Raporu</h1>
                    <p style={{ color: '#64748b', marginTop: '4px' }}>Müşterilerinizin gecikmiş ödemelerini ve borç dağılımını takip edin</p>
                </div>
                <Link to="/raporlar" style={{ textDecoration: 'none', color: '#06C167', fontWeight: '600' }}>← Raporlara Dön</Link>
            </div>

            <div style={styles.grid}>
                <div style={styles.card}>
                    <div style={styles.cardLabel}>Güncel (0-30 Gün)</div>
                    <div style={{ ...styles.cardValue, color: '#06C167' }}>{agingData.totals.current.toFixed(2)} ₺</div>
                </div>
                <div style={styles.card}>
                    <div style={styles.cardLabel}>31-60 Gün</div>
                    <div style={{ ...styles.cardValue, color: '#2563eb' }}>{agingData.totals.overdue30.toFixed(2)} ₺</div>
                </div>
                <div style={styles.card}>
                    <div style={styles.cardLabel}>61-90 Gün</div>
                    <div style={{ ...styles.cardValue, color: '#ea580c' }}>{agingData.totals.overdue60.toFixed(2)} ₺</div>
                </div>
                <div style={styles.card}>
                    <div style={styles.cardLabel}>90+ Gün</div>
                    <div style={{ ...styles.cardValue, color: '#dc2626' }}>{agingData.totals.overdue90.toFixed(2)} ₺</div>
                </div>
            </div>

            <div style={styles.tableContainer}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th}>Müşteri Adı</th>
                            <th style={styles.th}>Toplam Borç</th>
                            <th style={styles.th}>0-30 Gün</th>
                            <th style={styles.th}>31-60 Gün</th>
                            <th style={styles.th}>61-90 Gün</th>
                            <th style={styles.th}>90+ Gün</th>
                            <th style={styles.th}>Risk Durumu</th>
                        </tr>
                    </thead>
                    <tbody>
                        {agingData.list.map(customer => (
                            <tr key={customer.id}>
                                <td style={styles.td}>
                                    <Link to={`/musteriler/${customer.id}`} style={{ textDecoration: 'none', color: '#4f46e5', fontWeight: '600' }}>
                                        {customer.name}
                                    </Link>
                                </td>
                                <td style={{ ...styles.td, fontWeight: '700' }}>{customer.debt.toFixed(2)} ₺</td>
                                <td style={styles.td}>{customer.buckets.current.toFixed(2)} ₺</td>
                                <td style={styles.td}>{customer.buckets.overdue30.toFixed(2)} ₺</td>
                                <td style={styles.td}>{customer.buckets.overdue60.toFixed(2)} ₺</td>
                                <td style={styles.td}>{customer.buckets.overdue90.toFixed(2)} ₺</td>
                                <td style={styles.td}>
                                    {customer.buckets.overdue90 > (customer.debt * 0.5) ? (
                                        <span style={{ ...styles.badge, ...styles.redBadge }}>Yüksek Risk</span>
                                    ) : customer.buckets.overdue60 > 0 ? (
                                        <span style={{ ...styles.badge, ...styles.orangeBadge }}>Orta Risk</span>
                                    ) : (
                                        <span style={{ ...styles.badge, ...styles.greenBadge }}>Düşük Risk</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default AgingReport;
